import { apiFetch } from './client';
import { INTERFACE_TEXT } from '../constants/interfaceText';
import { rateLimitError } from '../utils/apiErrors';

export interface HelpResponse {
  meaning: string;
  form_id: string | null;
}

/**
 * Ask what a persona line meant (`POST /runs/{runId}/help`). Lessons send the
 * line's form id; a check-in sends none and the server explains the line it
 * holds. Errors carry a message a learner can read.
 */
export async function askHelp(
  runId: string,
  formId: string | null,
  signal?: AbortSignal
): Promise<HelpResponse> {
  if (signal?.aborted) {
    const abortError = new Error('Request aborted');
    abortError.name = 'AbortError';
    throw abortError;
  }
  const response = await apiFetch(`/runs/${runId}/help`, {
    method: 'POST',
    body: JSON.stringify({ form_id: formId }),
    signal,
  });
  if (response.ok) {
    return response.json();
  }
  const tooFast = await rateLimitError(response);
  if (tooFast) {
    throw tooFast;
  }
  if (response.status === 503) {
    throw new Error(INTERFACE_TEXT.helpUnavailable);
  }
  // 403 / 409 / 422 carry a plain-language `detail` written for the learner.
  if ([403, 409, 422].includes(response.status)) {
    const body = await response.json().catch(() => null);
    if (body && typeof body.detail === 'string' && body.detail) {
      throw new Error(body.detail);
    }
  }
  throw new Error(INTERFACE_TEXT.helpFailed);
}
